import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent } from 'components/input/editable-content/editable-content.component';
import { EditableContentDirective } from 'components/input/editable-content/editable-content.directive';
import { FileUploadComponent } from 'components/input/file-upload/file-upload';
import { IconType } from 'components/layout/icon/icon.component';
import { ListRowComponent } from 'components/layout/list-view/list-view.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { InputStateDirective } from 'directives/input-state.directive';
import { CredentialType } from 'enums/CredentialType';
import { ValidationType } from 'enums/validationType';
import { FileReferenceModel } from 'models/generated/Entities.GormCrm.FileReference';
import { TaskAttachmentModel } from 'models/generated/Entities.GormCrm.TaskAttachment';
import { DownloadFileInfoModel } from 'models/generated/Storage.Models.DownloadFileInfo';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription, firstValueFrom } from 'rxjs';
import { CredentialRequired } from 'services/credential.service';
import { FileStorageService } from 'services/file-storage.service';
import { FormValidationService } from 'services/form-validation.service';
import { LanguageService } from 'services/language.service';
import { ComponentUrl, NavigationService, UrlParam, UrlQuery } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { PushService } from 'services/push.service';
import { RelationService } from 'services/relation.service';
import { TaskService } from 'services/task.service';
import { ViewCompanyComponent } from './viewcompany.component';

interface Folder {
	name: string;
	path: string;
	date?: Date;
	children: Folder[];
	parent?: Folder;
	temporary: boolean;
	collapsed: boolean;
}

@Component({
	selector: 'list-company-files',
	templateUrl: './list-company-files.component.html',
	providers: [FormValidationService],
	imports: [Layout, NgTemplateOutlet, FormsModule, CustomSelectComponent, CustomInputComponent, ListView, ListRowComponent, NgClass, EditableContentDirective, EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent, InputStateDirective, FileUploadComponent, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('list-company-files')
@CredentialRequired(CredentialType.ManageCompanyFiles)
export class ListCompanyFilesComponent implements OnDestroy {

	@ViewChild('newFileNameInput') newFileNameInput?: CustomInputComponent;
	@ViewChild('fileNameEditor') private editor?: EditableContentComponent;
	@ViewChild(FileUploadComponent) uploader?: FileUploadComponent;
	@ViewChildren(EditableContentComponent) private editors?: EditableContentComponent[];
	public editorOffsetPosition?: ElementRef<HTMLElement>;

	private companySubscription: Subscription;
	private fileReferenceSubscription: Subscription;

	private _companyId?: string;
	@UrlParam
	@Input()
	set companyId(id: string | undefined) {
		this._companyId = id;
		if (id) {
			this.folderFiles = undefined;
			this.updateListWithFiles();
			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent).then(viewCompany => { viewCompany.companyId = id });
		}
	}
	get companyId() {
		return this._companyId;
	}

	public addNewFolder = false;
	public newFolderName = '';
	public files?: TaskAttachmentModel[];
	public previewFileUrl?: string;
	public selectedFiles: TaskAttachmentModel[] = [];
	public folderFiles?: TaskAttachmentModel[];
	public folders?: Folder[];
	public editNameOfFile?: TaskAttachmentModel;
	public renameFileExtension = '';
	public validationTypeEnum = ValidationType;
	public downloadProgress?: { processed: number, total: number };
	
	@ViewChild('folderNameInput') folderNameInput?: CustomInputComponent;
	@ViewChild('deleteButton') deleteButton?: ButtonComponent;
	@ViewChild('downloadButton') downloadButton?: ButtonComponent;

	private _previewFile?: TaskAttachmentModel;
	public set previewFile(file: TaskAttachmentModel | undefined) {
		if (this._previewFile === file) return;

		this._previewFile = file;
		this.previewFileUrl = undefined;
		if (file) this.fileStorageService.getDownloadUrl(file.fileReference!.filePath).then(url => this.previewFileUrl = url);
	}
	public get previewFile() { return this._previewFile; }

	@UrlQuery
	queryParams?: { currentPath: string };

	private _selectedFolder?: Folder;
	public set selectedFolder(folder: Folder | undefined) {
		if (folder) this.uncollapseFolder(folder);
		if (folder === this._selectedFolder) return;
		if (this.editors) this.editors.forEach(e => e.cancelEdit());
		this.folderFiles = undefined;
		this.previewFile = undefined;
		this._selectedFolder = folder;
		this.selectedFiles = [];
		this.queryParams = { currentPath: folder ? folder.path : '' };
		this.navigation.refreshUrl();
		if (!this.files || !folder) return;
		
		this.toggleAllFolderFiles(false);
		this.updateFolderFiles();
	}
	public get selectedFolder() { return this._selectedFolder; }

	folderItems: SelectListItemModel[] = [];

	constructor(
		readonly taskService: TaskService,
		readonly navigation: NavigationService,
		private readonly fileStorageService: FileStorageService,
		private readonly pushService: PushService,
		private readonly translate: TranslatePipe,
		private readonly notificationService: NotificationService,
		private readonly languageService: LanguageService) {

		this.companySubscription = this.pushService.onCompanyChanges.subscribe(event => {
			if (event.data.companyId === this.companyId) {
				this.updateListWithFiles(event.fromCurrentUser);
			}
		});
		this.fileReferenceSubscription = this.pushService.onFileReferenceChanges.subscribe(event => {
			const matchingFiles = this.files?.filter(f => event.data.fileReferenceIds.indexOf(f.fileReferenceId!) >= 0);
			if (matchingFiles && matchingFiles.length > 0) this.updateListWithFiles();
		});
	}
	ngOnDestroy(): void {
		this.companySubscription?.unsubscribe();
		this.fileReferenceSubscription?.unsubscribe();
	}

	public filename(folderFile: TaskAttachmentModel) {
		return folderFile.fileReference?.fileName.split('/').pop() || folderFile.fileReference?.fileName;
	}

	uncollapseFolder(folder: Folder) {
		folder.collapsed = false;
		if (!folder.parent) return;
		if (folder.parent.collapsed) this.uncollapseFolder(folder.parent);
	}
	
	toggleCollapseAllFolders() {
		if (!this.folders) return;
		let allFolders = RelationService.flattenArrayOfObjects<Folder>(this.folders[0].children, 'children');
		const collapsed = allFolders.some(f => f.collapsed);
		const toggleCollapse = (folder: Folder) => {
			folder.collapsed = !collapsed;
			folder.children.forEach(toggleCollapse);
		};
		this.folders.forEach(toggleCollapse);
		this.folders[0].collapsed = false; // Always keep the root folder expanded
	}


	async updateListWithFiles(removeDeletedFiles = false) {
		const files = await this.taskService.getCompanyTaskAttachments(this.companyId!);
		if (!this.files) this.files = files;
		else {
			for (const updatedFile of files) {
				const existingFile = this.files.find(f => f.id === updatedFile.id);
				if (existingFile) existingFile.fileReference = updatedFile.fileReference;
			}
			const removedFiles = this.files.filter(file => !files.some(f => f.id === file.id));
			const addedFiles = files.filter(file => !this.files!.some(f => f.id === file.id));
			for (const removedFile of removedFiles) {
				this.selectedFiles.splice(this.selectedFiles.indexOf(removedFile), 1);
				if (removeDeletedFiles) this.files.splice(this.files.indexOf(removedFile), 1);
				else removedFile.$deleted = true;
			}
			this.files.push(...addedFiles);
		}
		if (!this.folders) this.folders = [{ name: this.translate.transform('company.files.root-folder'), path: '', children: [], temporary: false, collapsed: false }];

		// Parse all files to add matching folder structure
		for (let file of this.files) {
			let currentFolder = this.folders[0];
			let path = file.fileReference!.fileName.split('/');
			path.pop(); // Remove file name
			while (path.length > 0) {
				const folderName = path.shift()!;
				const folder = currentFolder.children.find(f => f.name.toLowerCase() === folderName.toLowerCase());

				if (folder) {
					// update path if folder was renamed
					folder.path = currentFolder.path + folderName + '/';
					if (file.fileReference?.created) folder.date = (folder.date && folder.date < file.fileReference.created) ? folder.date : file.fileReference.created; // Folder's "date" is the oldest file in the folder
					currentFolder = folder;
					currentFolder.temporary = false;
				}
				else {
					const newFolder: Folder = { name: folderName, path: currentFolder.path + folderName + '/', children: [], parent: currentFolder, date: file.fileReference?.created, temporary: false, collapsed: true };
					currentFolder.children.push(newFolder);
					currentFolder.children.sort((a, b) => a.name.localeCompare(b.name, this.languageService.isoCode));
					currentFolder = newFolder;
				}
			}
		}
		// Remove all folders with no matching files
		const removeEmptyfolders = (parentFolder: Folder) => {
			for (let i = 0; i < parentFolder.children.length; i++) {
				const folder = parentFolder.children[i];
				if (!folder.temporary && this.allFilesInFolderAndSubFolders(folder).length === 0) {
					parentFolder.children.splice(i, 1);
					i--;
				}
				else removeEmptyfolders(folder);
			}
		};
		removeEmptyfolders(this.folders[0]);

		if (this.queryParams?.currentPath) 
			this.selectedFolder = this.findFolder(this.queryParams.currentPath);

		if (!this.selectedFolder || (!this.selectedFolder.temporary && this.allFilesInFolderAndSubFolders(this.selectedFolder).length === 0))
			this.selectedFolder = this.folders[0];

		this.updateFolderFiles();
		this.folderItems = this.getFoldersAsItems(this.folders[0]);
	}

	findFolder(path: string, folder: Folder = this.folders![0]): Folder | undefined {
		if (folder.path == path) return folder;
		for (let child of folder.children) {
			const found = this.findFolder(path, child);
			if (found) return found;
		}
		return undefined;
	}

	getFoldersAsItems(folder: Folder, items: SelectListItemModel[] = [], level: number = 0) {
		items.push(new SelectListItemModel(folder, (level > 0 ? "--".repeat(level) + " " : "") + folder.name));
		folder.children.sort((a, b) => a.name.localeCompare(b.name, this.languageService.isoCode)); // Sort folders by name
		for (let child of folder.children) {
			this.getFoldersAsItems(child, items, level + 1);
		}
		return items;
	}

	folderExists(folderName: string): boolean {
		if (!this.selectedFolder) return false;
		return this.selectedFolder.children.some(f => f.name.toLowerCase() === folderName.toLowerCase());
	}

	showAddNewFolder() {
		this.addNewFolder = true;
		this.newFolderName = '';
		setTimeout(() => {
			this.folderNameInput?.setFocus();
		});
	}

	createFolder() {
		if (this.newFolderName == '' || !this.selectedFolder) return;

		const newFolder: Folder = { name: this.newFolderName, path: this.selectedFolder.path + this.newFolderName + '/', children: [], parent: this.selectedFolder, temporary: true, collapsed: false };
		this.selectedFolder.children.push(newFolder);
		this.selectedFolder = newFolder;
		this.addNewFolder = false;
		this.newFolderName = '';
		this.folderItems = this.getFoldersAsItems(this.folders![0]);
	}

	updateFolderFiles() {
		if (!this.files || !this.selectedFolder) return;

		this.folderFiles = this.files.filter((file) => {
			const name = file.fileReference!.fileName.toLowerCase();
			return name.startsWith(this.selectedFolder!.path.toLowerCase()) && name.substring(this.selectedFolder!.path.length).indexOf('/') < 0;
		});
	}

	renameFolder(folder: Folder, newName: string) {
		const newFolderPath = folder.path.substring(0, folder.path.length - folder.name.length - 1) + newName + '/';
		folder.name = newName;
		if (!folder.temporary) {
			const allUpdatedFiles = this.allFilesInFolderAndSubFolders(folder)
				.map(file => {
					const updatedFileReference = Object.assign(new FileReferenceModel(), file.fileReference);
					updatedFileReference.fileName = `${newFolderPath}${updatedFileReference.fileName.substring(folder.path.length)}`;
					return updatedFileReference;
				});
			this.fileStorageService.updateFiles(allUpdatedFiles);
			return;
		}
	}
	renameFile(file?: TaskAttachmentModel, newName?: string) {
		if (!file || !newName) return;

		const updatedFileReference = Object.assign(new FileReferenceModel(), file.fileReference);
		newName = newName.trim() + this.renameFileExtension;
		const folderIndex = updatedFileReference.fileName.lastIndexOf('/');
		if (folderIndex >= 0) newName = updatedFileReference.fileName.substring(0, folderIndex + 1) + newName;

		updatedFileReference.fileName = newName;
		this.fileStorageService.updateFile(updatedFileReference);
	}

	toggleFile(file: TaskAttachmentModel) {
		const index = this.selectedFiles.indexOf(file);
		if (index > -1) this.selectedFiles.splice(index, 1);
		else this.selectedFiles.push(file);
	}
	toggleAllFolderFiles(checked: boolean) {
		if (!this.folderFiles) return;
		if (checked) {
			this.selectedFiles = [...this.folderFiles.filter(file => !file.$deleted)];
		} else {
			this.selectedFiles = [];
		}
	}
	allFilesAreSelected() : boolean {
		if (!this.folderFiles || this.folderFiles.length === 0) return false;
		return this.folderFiles.filter(file => !file.$deleted).length === this.selectedFiles.length &&
			!this.folderFiles.some(ff => this.selectedFiles.indexOf(ff) === -1);
	}

	async deleteSelectedFiles() {
		if (!this.selectedFiles || this.selectedFiles.length === 0) return;
		if (!await firstValueFrom(this.notificationService.confirm('company.files.confirm-delete-files', { 'fileCount': this.selectedFiles.length }))) {
			this.deleteButton?.reset();
			return;
		}

		// If all files in the folder are selected, mark the folder as temporary to prevent removing it from the current view
		if (this.selectedFolder && this.folderFiles && this.folderFiles?.filter(file => !this.selectedFiles.some(f => f.id === file.id)).length === 0) {
			this.selectedFolder.temporary = true;
		}

		await this.taskService.deleteTaskAttachments(this.selectedFiles.map(file => file.id!));
		this.selectedFiles = [];
		this.deleteButton?.succeeded();
	}

	async downloadSelectedFiles() {
		if (!this.selectedFiles || this.selectedFiles.length === 0) return;
		const filePaths = this.selectedFiles.map(file => {
			const downloadFileInfo = new DownloadFileInfoModel();
			downloadFileInfo.blobPath = file.fileReference!.filePath;
			downloadFileInfo.fullFilePath = this.filename(file)!;
			return downloadFileInfo;
		});
		if (filePaths.length == 1) {
			await this.fileStorageService.downloadFile(filePaths[0].blobPath);
			this.downloadButton?.succeeded();
			return;
		}

		this.downloadProgress = { processed: 0, total: filePaths.length };
		this.fileStorageService.downloadMultipleFiles(filePaths, this.selectedFolder?.name).subscribe(
			handledFiles => this.downloadProgress = { processed: handledFiles, total: filePaths.length },
			undefined,
			() => {
				this.downloadProgress = undefined;
				this.downloadButton?.succeeded();
			}
		);
	}
	enableRenameFile(file: TaskAttachmentModel, row?: ListRowComponent) {
		const filename = this.filename(file);
		if (!filename) return;
		const extensionIndex = filename.lastIndexOf('.');

		this.renameFileExtension = extensionIndex > 0 ? filename.substring(extensionIndex) : '';
		this.editNameOfFile = file;
		const newFileName = extensionIndex > 0 ? filename.substring(0, extensionIndex) : filename;

		this.editorOffsetPosition = row?.getRowElement();
		setTimeout(() => {
			setTimeout(() => this.editor?.beginEdit(newFileName));
		});
	}
	allFilesInFolderAndSubFolders(folder: Folder): TaskAttachmentModel[] {
		if (!this.files) return [];
		return this.files.filter(file => file.fileReference!.fileName.toLowerCase().startsWith(folder.path.toLowerCase()));
	}

	downloadFolder(folder: Folder) {
		const filePaths = this.allFilesInFolderAndSubFolders(folder)
			.map(file => {
				let downloadFileInfo = new DownloadFileInfoModel();
				downloadFileInfo.blobPath = file.fileReference!.filePath;
				downloadFileInfo.fullFilePath = folder.path != '' ? file.fileReference!.fileName.substring(folder.path.length) : file.fileReference!.fileName;
				return downloadFileInfo;
			});
		this.fileStorageService.downloadMultipleFiles(filePaths, folder.name);
	}
	async deleteFolder(folder: Folder) {
		if (!folder.temporary) {
			// Remove serverside "folder" by deleting every file whose name matches its path
			const filesInFolder = this.allFilesInFolderAndSubFolders(folder);
			if (!await firstValueFrom(this.notificationService.confirm('company.files.confirm-delete-folder', { 'folderName': folder.name, 'fileCount': filesInFolder.length }))) return;
			this.taskService.deleteTaskAttachments(filesInFolder.map(file => file.id!));
			return;
		}

		// Remove temporary (clientside) folder
		const removeRecursive = (folders: Folder[]) => {
			const index = folders.indexOf(folder);
			if (index >= 0) {
				folders.splice(index, 1);
				return;
			}
			for (const subFolder of folders) removeRecursive(subFolder.children);
		};
		if (this.folders) removeRecursive(this.folders);
	}

	openTaskflow(file: TaskAttachmentModel) {
		this.navigation.refreshView(ViewTaskflowComponent).then(view => {
			view.selectedActionId = file.taskActionId;
			view.taskflowId = file.taskflowId!;
		});
	}

	getStatusIcon(file: TaskAttachmentModel): IconType {
		let fileType = this.taskService.getTaskAttachmentFileType(file.fileReference?.fileName ?? '')
		switch (fileType) {
			case 'jpg':
			case 'jpeg':
			case 'png':
			case 'gif':
			case 'bmp':
			case 'svg':
			case 'raw':
			case 'webp':
				return 'file-image';
			case 'pdf':
				return 'file-pdf';
			case 'psd':
				return 'file-signature';
			case 'doc':
			case 'docx':
			case 'rtf':
				return 'file-word';
			case 'msg':
				return 'envelope';
			case 'mov':
			case 'avi':
			case 'mpg':
			case 'mpeg':
			case 'flv':
			case 'mkv':
			case 'mp4':
			case 'webm':
				return 'file-video';
			case 'zip':
			case '7z':
			case 'gzip':
			case 'rar':
			case 'tar':
				return 'file-zipper';
			default:
				return 'file';
		}
	}

	getStatusColor(file: TaskAttachmentModel) {
		switch (this.getStatusIcon(file)) {
			case 'file-image':
				return 'color-turquoise-50';
			case 'file-signature':
				return 'color-turquoise-70';
			case 'file-pdf':
				return 'color-red-50';
			case 'file-word':
				return 'color-blue-50';
			case 'envelope':
				return 'color-yellow-50';
			case 'file-video':
				return 'color-blue-50';
			case 'file-zipper':
				return 'color-orange-40';
			default:
				return 'color-grey-40';
		}
	}
}
